/**
 * ThemeSelector - Selector completo de temas de color para la aplicación
 * Adaptado de NewLnF/components/ThemeColorOptions.tsx para AdminConsole (JavaScript + MUI)
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  IconButton,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  Palette as PaletteIcon,
  Check as CheckIcon,
  LightbulbOutlined as LightbulbIcon,
  AutoAwesome as SparklesIcon,
  Visibility as EyeIcon,
  Add as PlusIcon,
  Edit as EditIcon,
  Brightness4,
  Brightness7,
} from "@mui/icons-material";
import { useAppRegistryStore } from "../../stores/appRegistryStore";
import { themeColorOptions, addTheme } from "../../data/themeColors";
import CreateNewTheme from "./CreateNewTheme";
import "./ThemeSelector.css";

const ThemeSelector = ({ isOpen, onClose }) => {
  const { settings, setThemeColor, setTheme, bumpNavbarVersion } = useAppRegistryStore();
  const [selectedTheme, setSelectedTheme] = useState(settings?.selectedTheme || "default");
  const [themeList, setThemeList] = useState(themeColorOptions);
  const [showThemeEditor, setShowThemeEditor] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [showCreateTheme, setShowCreateTheme] = useState(false);
  const isDarkMode = settings?.currentTheme === "dark";
  const currentTheme = themeList.find((t) => t.id === selectedTheme) || themeList[0];

  const handleThemeSelect = (themeId) => {
    const currentObj = themeList.find((t) => t.id === selectedTheme);
    const nextObj = themeList.find((t) => t.id === themeId);
    console.log('[Theme] change', {
      currentId: currentObj?.id,
      currentName: currentObj?.name,
      nextId: nextObj?.id,
      nextName: nextObj?.name,
    });

    if (themeId === "dark-mode" || themeId === "light-mode") {
      setTheme("light");
      return;
    }

    setTheme("light");
    setSelectedTheme(themeId);
    setThemeColor(themeId);
    bumpNavbarVersion();

    if (nextObj) {
      applyThemeColors(nextObj);
      setTimeout(() => applyThemeColors(nextObj), 100);
    }
  };

  const applyThemeColors = (theme) => {
    const root = document.documentElement;
    Object.entries(theme.colors).forEach(([key, value]) => {
      const cssVar = key.replace(/([A-Z])/g, "-$1").toLowerCase();
      root.style.setProperty(`--${cssVar}`, value);
    });
    
    // También mapear a las variables CSS globales que usa AdminConsole
    root.style.setProperty('--bg-primary', theme.colors.background || '#ffffff');
    root.style.setProperty('--text-primary', theme.colors.foreground || '#1a1a1a');
    root.style.setProperty('--card-bg', theme.colors.card || '#ffffff');
    
    // Crear/actualizar stylesheet dinámico para aplicar colores a Material-UI
    // Esto es necesario porque Material-UI no usa las variables CSS directamente
    // Insertarlo ANTES del stylesheet de modo oscuro (si existe) para que el modo oscuro tenga precedencia
    let styleSheet = document.getElementById('dynamic-theme-styles');
    const darkModeStyleSheet = document.getElementById('dark-mode-styles');
    
    if (!styleSheet) {
      styleSheet = document.createElement('style');
      styleSheet.id = 'dynamic-theme-styles';
      if (darkModeStyleSheet) {
        document.head.insertBefore(styleSheet, darkModeStyleSheet);
      } else {
        document.head.appendChild(styleSheet);
      }
    } else {
      styleSheet.remove();
      if (darkModeStyleSheet) {
        document.head.insertBefore(styleSheet, darkModeStyleSheet);
      } else {
        document.head.appendChild(styleSheet);
      }
    }
    
    // Aplicar colores con máxima especificidad para sobrescribir Material-UI
    // Usar selectores muy específicos incluyendo la clase que agregamos
    styleSheet.textContent = `
      /* Aplicar colores del tema a Material-UI con máxima especificidad */
      .theme-applied-navbar,
      header.MuiAppBar-root.MuiAppBar-root,
      .MuiAppBar-root.MuiAppBar-root[class*="MuiAppBar"],
      [class*="MuiAppBar-root"].MuiAppBar-root,
      .MuiAppBar-root {
        background-color: ${theme.colors.navbar} !important;
        color: ${theme.colors.foreground || '#1a1a1a'} !important;
      }
      .theme-applied-toolbar,
      .MuiToolbar-root.MuiToolbar-root,
      .MuiToolbar-root[class*="MuiToolbar"],
      [class*="MuiToolbar-root"].MuiToolbar-root,
      .MuiToolbar-root {
        background-color: ${theme.colors.toolbar || theme.colors.navbar} !important;
        color: ${theme.colors.foreground || '#1a1a1a'} !important;
      }
      body,
      html body {
        background-color: ${theme.colors.background || '#ffffff'} !important;
        color: ${theme.colors.foreground || '#1a1a1a'} !important;
      }
      /* Sobrescribir estilos inline de Material-UI - incluso con transparent */
      [style*="background-color"].theme-applied-navbar,
      [style*="background-color"].MuiAppBar-root {
        background-color: ${theme.colors.navbar} !important;
      }
      [style*="background-color"].theme-applied-toolbar,
      [style*="background-color"].MuiToolbar-root {
        background-color: ${theme.colors.toolbar || theme.colors.navbar} !important;
      }
    `;
    
    // También aplicar directamente a los elementos DOM para asegurar
    const applyDirectly = () => {
      const appBar = document.querySelector('header.MuiAppBar-root') || document.querySelector('.MuiPaper-root.MuiAppBar-root') || document.querySelector('.MuiAppBar-root');
      const toolbar = document.querySelector('.MuiToolbar-root');
      if (appBar) {
        appBar.style.setProperty('background-color', theme.colors.navbar, 'important');
        appBar.style.setProperty('color', theme.colors.foreground || '#1a1a1a', 'important');
      }
      if (toolbar) {
        const toolbarBg = theme.colors.toolbar || theme.colors.navbar;
        toolbar.style.setProperty('background-color', toolbarBg, 'important');
        toolbar.style.setProperty('color', theme.colors.foreground || '#1a1a1a', 'important');
      }
      if (document.body) {
        document.body.style.setProperty('background-color', theme.colors.background || '#ffffff', 'important');
        document.body.style.setProperty('color', theme.colors.foreground || '#1a1a1a', 'important');
      }
      const paperEl = document.querySelector('.MuiPaper-root.MuiAppBar-root') || document.querySelector('header.MuiAppBar-root');
      const toolbarEl = document.querySelector('.MuiToolbar-root');
      const paperBg = paperEl ? getComputedStyle(paperEl).backgroundColor : null;
      const toolbarBgComputed = toolbarEl ? getComputedStyle(toolbarEl).backgroundColor : null;
      console.log('[Theme] applied', {
        themeId: theme.id,
        themeName: theme.name,
        paperBg: paperBg,
        toolbarBg: toolbarBgComputed,
      });
    };
    
    applyDirectly();
  };

  useEffect(() => {
    const currentTheme = themeList.find((t) => t.id === selectedTheme);
    if (currentTheme) {
      applyThemeColors(currentTheme);
    }
  }, [selectedTheme, themeList]);

  // Aplicar tema al cargar el componente
  useEffect(() => {
    const currentTheme = themeList.find((t) => t.id === selectedTheme);
    if (currentTheme && isOpen) {
      // Pequeño delay para asegurar que los elementos estén renderizados
      setTimeout(() => {
        applyThemeColors(currentTheme);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedTheme, themeList]);

  const ThemePreview = ({ theme, isSelected }) => (
    <Paper
      elevation={isSelected ? 4 : 1}
      onClick={() => handleThemeSelect(theme.id)}
      sx={{
        position: "relative",
        cursor: "pointer",
        border: isSelected ? 2 : 1,
        borderColor: isSelected ? theme.colors.primary : isDarkMode ? "#404040" : "#e0e0e0",
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: 4,
          "& .edit-button": {
            opacity: 1,
          },
        },
        p: 2,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            height: 24,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            px: 1,
            fontSize: "0.75rem",
            fontWeight: 500,
            mb: 1,
            backgroundColor: theme.colors.navbar,
            color: theme.colors.foreground,
          }}
        >
          Navbar
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box
            sx={{
              height: 16,
              borderRadius: 0.5,
              display: "flex",
              alignItems: "center",
              px: 1,
              fontSize: "0.7rem",
              backgroundColor: theme.colors.header,
              color: theme.colors.background,
            }}
          >
            <Box sx={{ fontSize: "0.7rem", fontWeight: 500 }}>Header</Box>
            <Box
              sx={{
                ml: "auto",
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: theme.colors.accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: theme.colors.primary,
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              height: 32,
              borderRadius: 0.5,
              p: 0.5,
              backgroundColor: theme.colors.paper,
            }}
          >
            <Box sx={{ height: 4, borderRadius: 0.5, backgroundColor: theme.colors.muted, mb: 0.5 }} />
            <Box
              sx={{
                height: 4,
                borderRadius: 0.5,
                width: "75%",
                backgroundColor: theme.colors.mutedForeground,
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            height: 12,
            borderRadius: 0.5,
            mt: 1,
            backgroundColor: theme.colors.footer,
          }}
        />
      </Box>

      {isSelected && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 20,
            height: 20,
            borderRadius: "50%",
            backgroundColor: theme.colors.primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CheckIcon sx={{ fontSize: 14, color: "#ffffff" }} />
        </Box>
      )}

      {/* Botón de edición - aparece al hacer hover */}
      <IconButton
        className="edit-button"
        onClick={(e) => {
          e.stopPropagation();
          setEditingTheme(theme);
          setShowThemeEditor(true);
        }}
        sx={{
          position: "absolute",
          top: 8,
          left: 8,
          width: 24,
          height: 24,
          opacity: 0,
          transition: "opacity 0.2s ease",
          backgroundColor: isDarkMode ? "rgba(30, 30, 30, 0.8)" : "rgba(255, 255, 255, 0.8)",
          "&:hover": {
            backgroundColor: isDarkMode ? "rgba(30, 30, 30, 1)" : "rgba(255, 255, 255, 1)",
          },
        }}
        size="small"
      >
        <EditIcon sx={{ fontSize: 14, color: isDarkMode ? "#ffffff" : "#1a1a1a" }} />
      </IconButton>

      <Box>
        <Typography variant="body2" fontWeight={500} sx={{ color: isDarkMode ? "#ffffff" : "#1a1a1a" }}>
          {theme.name}
        </Typography>
        <Typography variant="caption" sx={{ color: isDarkMode ? "#b0b0b0" : "text.secondary" }}>
          {theme.description}
        </Typography>
      </Box>
    </Paper>
  );

  const handleSaveNewTheme = (newTheme) => {
    const existingIndex = themeList.findIndex((t) => t.id === newTheme.id);
    let updatedThemes;

    if (existingIndex >= 0) {
      // Update existing theme
      updatedThemes = [...themeList];
      updatedThemes[existingIndex] = newTheme;
    } else {
      // Add new theme
      updatedThemes = [...themeList, newTheme];
      addTheme(newTheme); // Also add to the global theme options
    }

    setThemeList(updatedThemes);

    // Apply the new theme immediately
    handleThemeSelect(newTheme.id);

    // Close the editor
    setShowThemeEditor(false);
    setEditingTheme(null);
  };

  const conservativeThemes = themeList.filter((theme) => theme.family === "conservative");
  const playfulThemes = themeList.filter((theme) => theme.family === "playful");
  const accessibilityThemes = themeList.filter((theme) => theme.family === "accessibility");

  // Si se está editando o creando un tema, mostrar el editor
  if (showCreateTheme || showThemeEditor) {
    return (
      <CreateNewTheme
        onClose={() => {
          setShowCreateTheme(false);
          setShowThemeEditor(false);
          setEditingTheme(null);
          onClose();
        }}
        onSave={handleSaveNewTheme}
        editingTheme={editingTheme}
      />
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: currentTheme?.colors?.background || "#ffffff",
          color: currentTheme?.colors?.foreground || "#1a1a1a",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          backgroundColor: currentTheme?.colors?.navbar || "#ffffff",
          color: currentTheme?.colors?.foreground || "#1a1a1a",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PaletteIcon sx={{ color: isDarkMode ? "#64b5f6" : "#1976d2" }} />
          <Box>
            <Typography variant="h5" component="div" sx={{ color: isDarkMode ? "#e0e0e0" : "#1a1a1a" }}>
              Opciones de Tema
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMode ? "#b0b0b0" : "text.secondary", mt: 0.5 }}>
              La opción seleccionada se aplicará a todos los elementos del layout (navbar, toolbar, etc.). También puedes crear tus propias opciones de tema y esquemas de color.
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: isDarkMode ? "#e0e0e0" : "#1a1a1a" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 3,
          overflow: "auto",
          backgroundColor: currentTheme?.colors?.background || "#ffffff",
          color: currentTheme?.colors?.foreground || "#1a1a1a",
          maxHeight: "calc(90vh - 200px)",
        }}
      >
        {/* Modo Oscuro / Claro */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            {isDarkMode ? (
              <Brightness7 sx={{ color: isDarkMode ? "#b0b0b0" : "text.secondary", fontSize: 20 }} />
            ) : (
              <Brightness4 sx={{ color: isDarkMode ? "#b0b0b0" : "text.secondary", fontSize: 20 }} />
            )}
            <Typography variant="h6" sx={{ color: isDarkMode ? "#ffffff" : "#1a1a1a" }}>
              Modo de Visualización
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {/* Modo Claro */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={!isDarkMode ? 4 : 1}
                onClick={() => handleThemeSelect("light-mode")}
                sx={{
                  position: "relative",
                  cursor: "pointer",
                  border: 2,
                  borderColor: !isDarkMode ? "#1976d2" : (isDarkMode ? "#404040" : "#e0e0e0"),
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 4,
                  },
                  p: 2,
                }}
              >
                {!isDarkMode && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      backgroundColor: "#1976d2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 14, color: "#ffffff" }} />
                  </Box>
                )}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      height: 24,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      px: 1,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                      backgroundColor: "#ffffff",
                      color: "#1a1a1a",
                    }}
                  >
                    Navbar
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Box
                      sx={{
                        height: 16,
                        borderRadius: 0.5,
                        display: "flex",
                        alignItems: "center",
                        px: 1,
                        fontSize: "0.7rem",
                        backgroundColor: "#f5f5f5",
                        color: "#1a1a1a",
                      }}
                    >
                      <Box sx={{ fontSize: "0.7rem", fontWeight: 500 }}>Header</Box>
                    </Box>
                    <Box
                      sx={{
                        height: 32,
                        borderRadius: 0.5,
                        p: 0.5,
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <Box sx={{ height: 4, borderRadius: 0.5, backgroundColor: "#e0e0e0", mb: 0.5 }} />
                      <Box
                        sx={{
                          height: 4,
                          borderRadius: 0.5,
                          width: "75%",
                          backgroundColor: "#b0b0b0",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ color: isDarkMode ? "#ffffff" : "#1a1a1a" }}>
                    Modo Claro
                  </Typography>
                  <Typography variant="caption" sx={{ color: isDarkMode ? "#b0b0b0" : "text.secondary" }}>
                    Interfaz con fondo claro
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Modo Oscuro */}
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={isDarkMode ? 4 : 1}
                onClick={() => handleThemeSelect("dark-mode")}
                sx={{
                  position: "relative",
                  cursor: "pointer",
                  border: 2,
                  borderColor: isDarkMode ? "#1976d2" : (isDarkMode ? "#404040" : "#e0e0e0"),
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 4,
                  },
                  p: 2,
                  backgroundColor: isDarkMode ? "#1e1e1e" : "#2d2d2d",
                }}
              >
                {isDarkMode && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      backgroundColor: "#1976d2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 14, color: "#ffffff" }} />
                  </Box>
                )}
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      height: 24,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      px: 1,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      mb: 1,
                      backgroundColor: "#1e1e1e",
                      color: "#ffffff",
                    }}
                  >
                    Navbar
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Box
                      sx={{
                        height: 16,
                        borderRadius: 0.5,
                        display: "flex",
                        alignItems: "center",
                        px: 1,
                        fontSize: "0.7rem",
                        backgroundColor: "#2d2d2d",
                        color: "#e0e0e0",
                      }}
                    >
                      <Box sx={{ fontSize: "0.7rem", fontWeight: 500 }}>Header</Box>
                    </Box>
                    <Box
                      sx={{
                        height: 32,
                        borderRadius: 0.5,
                        p: 0.5,
                        backgroundColor: "#121212",
                      }}
                    >
                      <Box sx={{ height: 4, borderRadius: 0.5, backgroundColor: "#404040", mb: 0.5 }} />
                      <Box
                        sx={{
                          height: 4,
                          borderRadius: 0.5,
                          width: "75%",
                          backgroundColor: "#666666",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ color: "#ffffff" }}>
                    Modo Oscuro
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#b0b0b0" }}>
                    Interfaz con fondo oscuro
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3, borderColor: isDarkMode ? "#404040" : "#e0e0e0" }} />

        {/* Conservative / Professional Themes */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <LightbulbIcon sx={{ color: isDarkMode ? "#b0b0b0" : "text.secondary", fontSize: 20 }} />
            <Typography variant="h6" sx={{ color: isDarkMode ? "#ffffff" : "#1a1a1a" }}>
              Conservador / Profesional
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMode ? "#b0b0b0" : "text.secondary" }}>
              ({conservativeThemes.length} temas)
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {conservativeThemes.map((theme) => (
              <Grid item xs={12} sm={6} md={4} key={theme.id}>
                <ThemePreview theme={theme} isSelected={selectedTheme === theme.id} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 3, borderColor: isDarkMode ? "#404040" : "#e0e0e0" }} />

        {/* Playful / Engaging Themes */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <SparklesIcon sx={{ color: isDarkMode ? "#b0b0b0" : "text.secondary", fontSize: 20 }} />
            <Typography variant="h6" sx={{ color: isDarkMode ? "#ffffff" : "#1a1a1a" }}>
              Divertido / Atractivo
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMode ? "#b0b0b0" : "text.secondary" }}>
              ({playfulThemes.length} temas)
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {playfulThemes.map((theme) => (
              <Grid item xs={12} sm={6} md={4} key={theme.id}>
                <ThemePreview theme={theme} isSelected={selectedTheme === theme.id} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 3, borderColor: isDarkMode ? "#404040" : "#e0e0e0" }} />

        {/* Accessibility Focused Themes */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <EyeIcon sx={{ color: isDarkMode ? "#b0b0b0" : "text.secondary", fontSize: 20 }} />
            <Typography variant="h6" sx={{ color: isDarkMode ? "#ffffff" : "#1a1a1a" }}>
              Enfocado en Accesibilidad
            </Typography>
            <Typography variant="body2" sx={{ color: isDarkMode ? "#b0b0b0" : "text.secondary" }}>
              ({accessibilityThemes.length} temas)
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {accessibilityThemes.map((theme) => (
              <Grid item xs={12} sm={6} md={4} key={theme.id}>
                <ThemePreview theme={theme} isSelected={selectedTheme === theme.id} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          backgroundColor: currentTheme?.colors?.footer || currentTheme?.colors?.background || "#ffffff",
          color: currentTheme?.colors?.foreground || "#1a1a1a",
          borderTop: `1px solid ${currentTheme?.colors?.border || '#e0e0e0'}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mr: "auto" }}>
          <Button
            startIcon={<PlusIcon />}
            variant="outlined"
            onClick={() => {
              setShowCreateTheme(true);
            }}
            sx={{
              borderColor: isDarkMode ? "#404040" : "#e0e0e0",
              color: isDarkMode ? "#e0e0e0" : "#1a1a1a",
            }}
          >
            Crear Nuevo Tema
          </Button>
          <Typography variant="body2" sx={{ color: isDarkMode ? "#b0b0b0" : "text.secondary" }}>
            {themeList.length} temas disponibles
          </Typography>
        </Box>
        <Button onClick={onClose} variant="contained" color="primary">
          Listo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ThemeSelector;
